/**
 * Lazy topic-tree picker for the "Create revision session" dialog.
 *
 * Visual language mirrors `ReviseContentPicker` (the "By content" tab) — same
 * ExpandToggle chevron, same checkbox, same Collapsible motion, same row
 * layout + hover background. The differences are semantic:
 *
 *   - Nodes are loaded lazily per level via the `/me/curriculum-tree/children`
 *     endpoint rather than pre-computed from a full tree response.
 *   - Selection is "subtree scope" — ticking a node means "revise everything
 *     tagged to this node or any descendant". The backend dedupes when
 *     multiple selected nodes overlap (e.g. Cardiology + Arrhythmias).
 *   - We DON'T compute indeterminate state on parents based on child
 *     selection, because we don't know what descendants exist without
 *     expanding the subtree. A node is simply checked or unchecked; its
 *     `availableStepCount` already reflects the whole subtree.
 *   - Zero-step subtrees are dim and non-selectable (learner has no
 *     revisable content tagged there yet).
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight } from 'lucide-react';
import type { CurriculumTreeNode } from '@formency/shared';
import { Checkbox } from '@/interfaces/learner/ui';
import { useCurriculumTreeChildren } from '@/shared/api/hooks';

interface TopicPickerProps {
  selectedNodeIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
}

export default function TopicPicker({ selectedNodeIds, onSelectionChange }: TopicPickerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelected = (id: string) => {
    const next = new Set(selectedNodeIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  return (
    <div
      className="flex flex-col gap-0.5"
      style={{
        maxHeight: 420,
        overflowY: 'auto',
        padding: 8,
        borderRadius: 10,
        background: 'var(--color-surface-solid)',
        border: '1px solid var(--color-border)',
      }}
    >
      <TreeLevel
        parentId={undefined}
        depth={0}
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        selectedNodeIds={selectedNodeIds}
        toggleSelected={toggleSelected}
      />
    </div>
  );
}

// ─── Tree level ───────────────────────────────────────────────────────────────

function TreeLevel({
  parentId,
  depth,
  expanded,
  toggleExpanded,
  selectedNodeIds,
  toggleSelected,
}: {
  parentId: string | undefined;
  depth: number;
  expanded: Set<string>;
  toggleExpanded: (id: string) => void;
  selectedNodeIds: Set<string>;
  toggleSelected: (id: string) => void;
}) {
  const { data, isLoading } = useCurriculumTreeChildren(parentId);
  const nodes = data?.nodes ?? [];

  if (isLoading) {
    return (
      <div
        className="px-2 py-1.5"
        style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}
      >
        Loading…
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div
        className="px-2 py-1.5"
        style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}
      >
        {depth === 0 ? 'No topics in your library yet.' : 'No subtopics here.'}
      </div>
    );
  }

  return (
    <>
      {nodes.map((node) => (
        <NodeRow
          key={node.id}
          node={node}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          selectedNodeIds={selectedNodeIds}
          toggleSelected={toggleSelected}
        />
      ))}
    </>
  );
}

// ─── Node row ─────────────────────────────────────────────────────────────────

function NodeRow({
  node,
  expanded,
  toggleExpanded,
  selectedNodeIds,
  toggleSelected,
}: {
  node: CurriculumTreeNode;
  expanded: Set<string>;
  toggleExpanded: (id: string) => void;
  selectedNodeIds: Set<string>;
  toggleSelected: (id: string) => void;
}) {
  const isOpen = expanded.has(node.id);
  const isSelected = selectedNodeIds.has(node.id);
  // Top-level / domain check uses depth === 1 because the current curriculum
  // convention stores roots at depth 1 (not 0 as the schema doc originally
  // suggested). Checked structurally rather than by prop to keep the picker's
  // bolding consistent with which nodes are actually domains.
  const isTopLevel = node.depth === 1;

  // Every node returned by the service has availableStepCount > 0 (the SQL
  // inner-joins against the user's library step-set and drops zero-count
  // subtrees), so no dim/disabled state is rendered here. If that invariant
  // ever breaks, the node just shows an accurate step count anyway.

  return (
    <div>
      <div
        className="flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors duration-150"
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-surface-muted)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {node.hasChildren ? (
          <ExpandToggle expanded={isOpen} onClick={() => toggleExpanded(node.id)} />
        ) : (
          <div className="h-5 w-5 shrink-0" />
        )}
        <TreeCheckbox checked={isSelected} onChange={() => toggleSelected(node.id)} />
        <span
          className={`min-w-0 flex-1 truncate ${isTopLevel ? 'font-semibold' : ''}`}
          style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
        >
          {node.displayName}
        </span>
        <span
          className="shrink-0"
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}
        >
          {node.availableStepCount}q
        </span>
      </div>
      {node.hasChildren && (
        <Collapsible open={isOpen}>
          <div className="ml-6">
            <TreeLevel
              parentId={node.id}
              depth={node.depth + 1}
              expanded={expanded}
              toggleExpanded={toggleExpanded}
              selectedNodeIds={selectedNodeIds}
              toggleSelected={toggleSelected}
            />
          </div>
        </Collapsible>
      )}
    </div>
  );
}

// ─── Reusable primitives (matched to ReviseContentPicker's styling) ──────────

function TreeCheckbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return <Checkbox aria-label="Select topic" size="sm" isSelected={checked} onChange={onChange} />;
}

function ExpandToggle({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded transition-transform duration-200"
      style={{ color: 'var(--color-text-muted)' }}
    >
      <ChevronRight
        size={14}
        style={{
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 200ms ease',
        }}
      />
    </button>
  );
}

function Collapsible({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          style={{ overflow: 'hidden' }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
